import isEmpty from 'lodash/isEmpty';
import isIBAN from 'validator/lib/isIBAN';
import { z } from 'zod';
import { PAYMENT_RECIPIENT_MAX_LENGTH } from '~/utils/payment';
import { Account } from './account';
import { Company } from './company';
import { Document, DocumentDirection } from './document';
import { Flow, PaymentMethod } from './flow';
import { Money } from './money';

export const RecurrenceResourceTypes = 'recurrence';

export const RecurrenceQueryKeys = {
  all: ['Recurrences'],
  companyList: (companyId: string) => ['Recurrences', { companyId }],
  chart: (companyId: string, count: number, accountId: string[] | null) => [
    'Recurrences',
    { companyId },
    'chart',
    count,
    accountId,
  ],
};

export enum Frequency {
  weekly = 'WEEKLY',
  biweekly = 'BIWEEKLY',
  monthly = 'MONTHLY',
  bimonthly = 'BIMONTHLY',
  quarterly = 'QUARTERLY',
  semiannual = 'SEMIANNUAL',
  annual = 'ANNUAL',
}

export interface Recurrence {
  id: string;
  amount: Money;
  description: string;
  direction: Document['direction'];
  paymentMethod: Flow['paymentMethod'] | null;
  frequency: Frequency;
  frequencyParams: number;
  startDate: string;
  endDate: string | null;
  company: Company;
  account: Account | null;
  category?: Document['category'] | null;
  subcategory?: Document['subcategory'] | null;
  payeeName: string | null;
  payeeIdentifier: string | null;
}

interface ResourceId {
  id: string;
}

export type RecurrenceCreate = Omit<Recurrence, 'id' | 'account' | 'company' | 'category' | 'subcategory'> & {
  account: ResourceId | null;
  company: ResourceId;
  category: ResourceId | null;
  subcategory: ResourceId | null;
};

export enum DeleteKind {
  all = 'all',
  future = 'future',
}

export const recurrenceSchema = z.object({
  amount: z.string().min(1),
  description: z.string().min(1),
  paymentMethod: z.nativeEnum(PaymentMethod).nullable(),
  category: z.string().nullish(),
  subcategory: z.string().nullish(),
  direction: z.nativeEnum(DocumentDirection),
  account: z.string().nullable(),
  payeeName: z.string().trim().max(PAYMENT_RECIPIENT_MAX_LENGTH).optional(),
  payeeIdentifier: z
    .string()
    .trim()
    .refine((value) => isEmpty(value) || isIBAN(value), {
      params: { issue: 'invalid_iban' },
    })
    .optional(),
  frequency: z
    .object({
      start: z.date(),
      end: z.date().optional().nullable(),
      mode: z.nativeEnum(Frequency),
      hasEnd: z.boolean(),
      param: z.coerce.number(),
    })
    .superRefine((value, ctx) => {
      if (value.hasEnd && value.end != null && value.end <= value.start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['end'],
          params: { issue: 'end_before_start' },
        });
      }
    }),
});

export type RecurrenceForm = z.infer<typeof recurrenceSchema>;
